<template>
  <button
    type="button"
    class="onboarding-progress-card"
    :class="{ 'onboarding-progress-card--disabled': disabled }"
    :disabled="disabled"
    @click="$emit('select', progress)"
  >
    <img
      class="onboarding-progress-card__image"
      :src="progress.image"
      :alt="progress.imageAlt || progress.label"
    />

    <span class="onboarding-progress-card__label">
      {{ progress.label }}
    </span>

    <span
      class="onboarding-progress-card__progress"
      :class="{
        'onboarding-progress-card__progress--complete': completed,
      }"
    >
      <span
        v-if="completed"
        class="onboarding-progress-card__complete-icon"
        aria-hidden="true"
      >
        <span class="onboarding-progress-card__complete-check"></span>
      </span>
      {{ progressLabel }}
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { OnboardingProgressDisplay } from "@/components/Onboarding/OnboardingProgressDisplay";

export default defineComponent({
  name: "OnboardingProgressCard",
  props: {
    progress: {
      type: Object as PropType<OnboardingProgressDisplay>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    select: (_progress: OnboardingProgressDisplay) => true,
  },
  computed: {
    completed(): boolean {
      return (
        (this.progress.current === -1 && this.progress.total === -1)
      );
    },
    progressLabel(): string {
      if (this.completed) {
        return "Terminé";
      }
      if (this.progress.current === 0 && this.progress.total === 0) {
        return "Lecture";
      }
      return `${this.progress.current} sur ${this.progress.total}`;
    },
  },
});
</script>

<style scoped>
.onboarding-progress-card {
  width: 100%;
  min-height: 188px;
  padding: 24px 23px 28px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid rgb(var(--v-theme-grey90, 225, 227, 229));
  border-radius: 7px;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.onboarding-progress-card:hover {
  border-color: rgb(var(--v-theme-primary70, 240, 123, 125));
  box-shadow: 0 8px 22px rgba(23, 26, 28, 0.08);
  transform: translateY(-1px);
}

.onboarding-progress-card:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary50, 228, 39, 36));
  outline-offset: 2px;
}

.onboarding-progress-card--disabled {
  cursor: default;
}

.onboarding-progress-card--disabled:hover {
  border-color: rgb(var(--v-theme-grey90, 225, 227, 229));
  box-shadow: none;
  transform: none;
}

.onboarding-progress-card__image {
  width: 64px;
  height: 64px;
  object-fit: contain;
  margin-bottom: 14px;
}

.onboarding-progress-card__label {
  max-width: 100%;
  color: rgb(var(--v-theme-grey10, 23, 26, 28));
  font-size: 16px;
  font-weight: 500;
  line-height: 1.28;
  overflow-wrap: anywhere;
}

.onboarding-progress-card__progress {
  margin-top: 3px;
  color: rgb(var(--v-theme-success40, 12, 91, 77));
  font-size: 14px;
  font-weight: 400;
  line-height: 1.25;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.onboarding-progress-card__progress--complete {
  color: #5f8910;
}

.onboarding-progress-card__complete-icon {
  width: 12px;
  height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  background: #7eb715;
}

.onboarding-progress-card__complete-check {
  width: 6px;
  height: 4px;
  border-left: 1.5px solid #ffffff;
  border-bottom: 1.5px solid #ffffff;
  transform: rotate(-45deg) translate(0.5px, -0.5px);
}
</style>
